import { useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import { connect } from "react-redux";
import { get } from "lodash";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SidenavCollapse from "layouts/Sidenav/SidenavCollapse";
import SidenavRoot from "layouts/Sidenav/SidenavRoot";
import sidenavLogoLabel from "layouts/Sidenav/styles/sidenav";
import { UIController, setMiniSidenav } from "context";
import { useLocale } from "context/locale.context";
import store from "store";
import { store as storeRedux, history } from "redux/store";
import config from "config/config";

const mapStateToProps = ({ monitoring, setting }) => {
  const { generalList } = monitoring;
  const { detail } = setting;
  return { list: generalList, detail };
};

function Sidenav({ list, detail, color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = UIController();
  const { miniSidenav, transparentSidenav } = controller;
  const { t } = useLocale();
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    storeRedux.dispatch({
      type: "monitoring/LIST_GENERAL",
      payload: {
        page: 1,
        pageSize: 1000,
        filters: JSON.stringify([["type", "=", "general"]]),
      },
    });
  }, []);

  useEffect(() => {
    storeRedux.dispatch({
      type: "setting/DETAIL",
    });
  }, []);

  /* eslint-disable indent */
  brandName = get(detail, ["brandName"]) ? get(detail, ["brandName"]) : "";
  brand = get(detail, ["logo"], "") ? `${config.API_ENDPOINT}/uploaded/${get(detail, ["logo", "path"])}` : brand;

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, route, href, roles }) => {
    let returnValue;
    const user = store.get("user");
    let role;
    if (user) {
      role = user.role;
    } else {
      history.push("/login");
      store.clearAll();
    }

    if (type === "collapse" && roles.includes(role)) {
      returnValue = href ? (
        <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
            <SidenavCollapse
              color={color}
              name={t(name)}
              icon={icon}
            active={key === collapseName || key === pathname}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink to={route} key={key}>
          <SidenavCollapse
            color={color}
            key={key}
            name={t(name)}
            icon={icon}
            active={key === collapseName || key === pathname}
            noCollapse={noCollapse}
          />
        </NavLink>
      );
    } else if (type === "title" && roles.includes(role)) {
      returnValue = (
        <SoftTypography
          key={key}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          opacity={0.6}
          pl={3}
          mt={1}
          mb={1}
          ml={1}
        >
          {t(title)}
        </SoftTypography>
      );
    } else if (type === "divider") {
      returnValue = <Divider key={key} />;
    }

    return returnValue;
  });

  return (
    <SidenavRoot {...rest} dispatch="" variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      <SoftBox pt={2} px={4} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SoftTypography>
        </SoftBox>
        <SoftBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <SoftBox component="img" src={brand} alt="Datalogger Logo" width="2rem" />}
          <SoftBox width={!brandName && "100%"} sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}>
            <SoftTypography component="h6" variant="button" fontWeight="bold" mt={0.5}>
              {brandName}
              {/* <sub style={{ fontSize: "10px" }}>IOT</sub> */}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </SoftBox>
      <Divider />
      <List>
        <SoftTypography
          key="dashboad-pages"
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          opacity={0.6}
          pl={3}
          mt={1}
          mb={1}
          ml={1}
        >
          {t("Dashboards")}
        </SoftTypography>
        <NavLink to="/dashboard" key="dashboard">
          <SidenavCollapse
            color={color}
            key="dashboard"
            name={t("Dashboard")}
            icon={<Icon>dashboard</Icon>}
            active={"dashboard" === collapseName || "dashboard" === pathname}
            noCollapse={true}
          />
        </NavLink>
        {list && list.length > 0 && (
          <SoftTypography
            key="monitoring-pages"
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            opacity={0.6}
            pl={3}
            mt={1}
            mb={1}
            ml={1}
          >
            {t("Monitorings")}
          </SoftTypography>
        )}
        {list &&
          list.map((l) => (
            <NavLink
              to={{ pathname: `/monitoring/${l.id}`, state: { monitoringKey: l.key } }}
              key={`/monitoring/${l.id}`}
            >
              <SidenavCollapse
                color={color}
                key={`/monitoring/${l.id}`}
                name={l.name}
                icon={<Icon>bar_chart</Icon>}
                active={`/monitoring/${l.id}` === collapseName || `/monitoring/${l.id}` === pathname}
                noCollapse={true}
              />
            </NavLink>
          ))}
        {renderRoutes}
      </List>
      <SoftBox pt={2} my={2} mx={2} mt="auto">
        <SoftBox mt={2}>
          <SoftButton
            // component="a"
            // href="https://ubisen.com"
            // target="_blank"
            // rel="noreferrer"
            style={{ pointerEvents: "none", textTransform: "none" }}
            variant="gradient"
            color={color}
            fullWidth
          >
            uSense {get(detail, ["backendVersion"], "")}
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  list: PropTypes.array.isRequired,
  detail: PropTypes.object.isRequired,
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default connect(mapStateToProps)(Sidenav);
